import { putPhoto, requestIncidentPhotoUploadUrls } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import { Button } from '@jq4race/ui';
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CameraCapture } from './CameraCapture';
import type { IncidentPhotoStatus } from './IncidentPhotoSlot';
import { IncidentPhotoSlot } from './IncidentPhotoSlot';

export interface IncidentPhoto {
  id: string;
  status: IncidentPhotoStatus;
  key?: string;
  thumbnailUrl?: string;
  errorMessage?: string;
  blob?: Blob;
}

/**
 * IncidentPhotoSink (third adversarial review finding H3) — the LOCAL
 * storage port `IncidentPhotos` uses instead of uploading straight to R2 at
 * capture time, when `sink` is provided. `slotId` is THIS component's own
 * stable per-photo id (e.g. "incident-1") — a retake reuses the SAME
 * `slotId`, so a `LocalIncidentPhotoSink` overwrites the previous capture in
 * place rather than leaving an orphan behind.
 */
export interface IncidentPhotoSink {
  upload(slotId: string, blob: Blob): Promise<{ key: string }>;
  /**
   * Fourth adversarial review, LOW #4 — deletes the durable local blob for
   * `slotId` (its role in this session is over). Optional so the default
   * remote-upload-at-capture mode (no `sink`) never needs it — a remote
   * photo has nothing local to delete. Best-effort by design: a failure
   * here is swallowed by `handleRemove` (never blocks the UI removal) — an
   * orphan left behind is still caught by `cleanupOrphanedBlobs`'s own
   * 24h-grace sweep, so this is purely an EARLY-cleanup optimization, never
   * a correctness requirement.
   */
  delete?(slotId: string): Promise<void>;
}

export interface IncidentPhotosProps {
  /**
   * Called with the current list of successfully-captured photo
   * identifiers, in order — server storage keys in the default (no `sink`)
   * remote-upload-at-capture mode, or LOCAL blob ids (`item.incidentPhotoRefs`,
   * design H3) when `sink` is provided.
   */
  onKeysChange: (keys: string[]) => void;
  /** Soft cap on how many incident photos may be attached (spec: 20). */
  maxCount?: number;
  /**
   * Third adversarial review finding H3 — when provided, incident photos
   * are durably stored LOCALLY (compressed, linked to the outbox item,
   * cleaned up by `incidentPhotoRefs` membership) and uploaded fresh at
   * SYNC time instead, exactly like the 4 mandatory angles. Omitted (the
   * default) keeps today's remote-upload-at-capture behavior — Flash's own
   * `<IncidentPhotos>` usage passes no `sink` and is UNAFFECTED.
   */
  sink?: IncidentPhotoSink;
}

const DEFAULT_MAX_COUNT = 20;

let idSeq = 0;
function nextId(): string {
  idSeq += 1;
  return `incident-${idSeq}`;
}

/**
 * Array-based, angle-less "incident" (pre-existing damage) photo capture
 * section (ticket-incident-photos, Slice 2). Fully optional — zero photos is
 * a valid, submittable state; the ticket-submit gate MUST NOT depend on this
 * component (enforced by the caller, not here).
 *
 * Reuses `CameraCapture` (generalized via its optional `label` prop) for the
 * actual viewfinder/shutter UX, and issues one presigned upload target per
 * capture via `requestIncidentPhotoUploadUrls(1)` — mirrors the transparent
 * single-retry behavior of the mandatory 4-angle flow in CheckinPage.
 */
export function IncidentPhotos({
  onKeysChange,
  maxCount = DEFAULT_MAX_COUNT,
  sink,
}: IncidentPhotosProps) {
  const { t } = useTranslation('checkin');
  const [photos, setPhotos] = useState<IncidentPhoto[]>([]);
  const [cameraOpenFor, setCameraOpenFor] = useState<string | null>(null);
  // Snapshot of the photo being retaken, so a cancelled retake can restore it
  // exactly (vs. a cancelled *new* add, which drops the empty slot entirely).
  const retakeSnapshotRef = useRef<IncidentPhoto | null>(null);

  // Keep the parent's key list in sync whenever the done-set changes.
  useEffect(() => {
    const keys = photos.filter((p) => p.status === 'done' && p.key).map((p) => p.key as string);
    onKeysChange(keys);
  }, [photos, onKeysChange]);

  async function uploadPhoto(id: string, blob: Blob, isRetry = false): Promise<void> {
    const thumbnailUrl = URL.createObjectURL(blob);
    setPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status: 'uploading', blob, thumbnailUrl } : p)),
    );

    try {
      let key: string;
      if (sink) {
        // Third adversarial review finding H3 — durable LOCAL store, no
        // network call at capture time; uploaded fresh at sync.
        ({ key } = await sink.upload(id, blob));
      } else {
        const { uploads } = await requestIncidentPhotoUploadUrls(1);
        const target = uploads[0];
        if (!target) throw new Error('No incident photo upload target issued');
        await putPhoto(target.url, blob);
        key = target.key;
      }

      setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, status: 'done', key } : p)));
    } catch {
      if (!isRetry) {
        // Transparent single retry, mirroring uploadSlot in CheckinPage.
        try {
          await uploadPhoto(id, blob, true);
          return;
        } catch {
          // fall through to error state
        }
      }
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === id
            ? { ...p, status: 'error', errorMessage: t('incidentPhotos.uploadFailed') }
            : p,
        ),
      );
    }
  }

  // Stable ref so camera callbacks can depend on cameraOpenFor only.
  const uploadPhotoRef = useRef(uploadPhoto);
  uploadPhotoRef.current = uploadPhoto;

  const handleAdd = useCallback(() => {
    setPhotos((prev) => {
      if (prev.length >= maxCount) return prev;
      const id = nextId();
      retakeSnapshotRef.current = null;
      setCameraOpenFor(id);
      return [...prev, { id, status: 'capturing' }];
    });
  }, [maxCount]);

  const handleRetake = useCallback((id: string) => {
    setPhotos((prev) => {
      const current = prev.find((p) => p.id === id) ?? null;
      retakeSnapshotRef.current = current;
      return prev.map((p) => (p.id === id ? { ...p, status: 'capturing' } : p));
    });
    setCameraOpenFor(id);
  }, []);

  const handleRemove = useCallback(
    (id: string) => {
      setPhotos((prev) => {
        const target = prev.find((p) => p.id === id);
        if (target?.thumbnailUrl) URL.revokeObjectURL(target.thumbnailUrl);
        return prev.filter((p) => p.id !== id);
      });
      // Fourth adversarial review, LOW #4 — delete the durable local blob
      // right away instead of leaving it for cleanupOrphanedBlobs' 24h
      // grace sweep. Best-effort: never blocks/breaks the UI removal above.
      void sink?.delete?.(id).catch(() => {
        // Swallowed — the orphan sweep is the correctness backstop.
      });
    },
    [sink],
  );

  const handleCameraCapture = useCallback(
    (blob: Blob) => {
      const id = cameraOpenFor;
      setCameraOpenFor(null);
      if (!id) return;
      void uploadPhotoRef.current(id, blob);
    },
    [cameraOpenFor],
  );

  const handleCameraCancel = useCallback(() => {
    const id = cameraOpenFor;
    const snapshot = retakeSnapshotRef.current;
    retakeSnapshotRef.current = null;
    setCameraOpenFor(null);
    if (!id) return;

    setPhotos((prev) => {
      if (snapshot) {
        // Cancelled retake — restore the pre-retake photo exactly.
        return prev.map((p) => (p.id === id ? snapshot : p));
      }
      // Cancelled a fresh add — drop the empty slot entirely.
      return prev.filter((p) => p.id !== id);
    });
  }, [cameraOpenFor]);

  const atCap = photos.length >= maxCount;

  return (
    <section className="space-y-3" aria-label={t('incidentPhotos.title')}>
      <div>
        <p className="jq-body-sm font-semibold text-foreground">{t('incidentPhotos.title')}</p>
        <p className="jq-caption text-muted-foreground">{t('incidentPhotos.description')}</p>
      </div>

      {cameraOpenFor ? (
        <CameraCapture
          label={t('incidentPhotos.cameraLabel')}
          onCapture={handleCameraCapture}
          onCancel={handleCameraCancel}
        />
      ) : (
        <>
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {photos.map((photo) => (
                <IncidentPhotoSlot
                  key={photo.id}
                  status={photo.status}
                  thumbnailUrl={photo.thumbnailUrl}
                  errorMessage={photo.errorMessage}
                  onRemove={() => handleRemove(photo.id)}
                  onRetake={() => handleRetake(photo.id)}
                />
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="ghost"
            className="h-12 w-full text-[14px]"
            onClick={handleAdd}
            disabled={atCap}
          >
            <Plus className="size-4" />
            {atCap ? t('incidentPhotos.limitReached', { maxCount }) : t('incidentPhotos.addPhoto')}
          </Button>
        </>
      )}
    </section>
  );
}
